import {
  Box,
  Divider,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Tr,
} from "@chakra-ui/react";
import { useParams } from "react-router";

import { fieldIcons } from "@/common/components/AttributeFilters";
import EmptyState from "@/common/components/EmptyState";
import { SectionHeading } from "@/common/components/SectionHeading";
import TimestampCell from "@/common/components/TimestampCell";
import { useUserData } from "@/user/data/useUserData";
import { UserDetailsDTO } from "@reflag/shared/userAPI";

export default function UserAttributes() {
  const { userId } = useParams();
  const { data: user, isLoading } = useUserData(userId);

  if (isLoading) return <EmptyState isLoading={isLoading} />;

  return (
    <Flex direction="column" fontSize="sm" gap={5}>
      <SystemAttributes user={user} />
      <Divider />
      <CustomAttributes user={user} />
    </Flex>
  );
}

function SystemAttributes({ user }: { user?: UserDetailsDTO }) {
  return (
    <Box>
      <SectionHeading mb={4}>System attributes</SectionHeading>
      <>
        <Table size="xs" sx={{ tableLayout: "fixed" }} variant="unstyled">
          <Tbody>
            <Tr>
              <TdIcon>{fieldIcons.id}</TdIcon>
              <TdKey>User ID</TdKey>
              <TdValue>{user?.id}</TdValue>
            </Tr>
            {user?.name && (
              <Tr>
                <TdIcon>{fieldIcons.text}</TdIcon>
                <TdKey>Name</TdKey>
                <TdValue>{user?.name}</TdValue>
              </Tr>
            )}
            {user?.email && (
              <Tr>
                <TdIcon>{fieldIcons.text}</TdIcon>
                <TdKey>Email</TdKey>
                <TdValue>{user?.email}</TdValue>
              </Tr>
            )}
            <Tr>
              <TdIcon>{fieldIcons.calendar}</TdIcon>
              <TdKey>First seen</TdKey>
              <TdValue>
                <TimestampCell value={user?.firstSeen} />
              </TdValue>
            </Tr>
            <Tr>
              <TdIcon>{fieldIcons.calendar}</TdIcon>
              <TdKey>Last seen</TdKey>
              <TdValue>
                <TimestampCell value={user?.lastSeen} />
              </TdValue>
            </Tr>
          </Tbody>
        </Table>
      </>
    </Box>
  );
}

function CustomAttributes({ user }: { user?: UserDetailsDTO }) {
  const attributes = Object.entries(user?.attributes ?? {});
  const filteredAttributes = attributes.sort(([a], [b]) => a.localeCompare(b));

  return (
    <Box>
      <SectionHeading mb={4}>Custom attributes</SectionHeading>
      <>
        {filteredAttributes.length === 0 ? (
          <Text color="gray.500" fontSize="sm">
            No attributes set for this user
          </Text>
        ) : (
          <Table size="xs" sx={{ tableLayout: "fixed" }} variant="unstyled">
            <Tbody>
              {filteredAttributes.map(([key, value]) => (
                <Tr key={key}>
                  <TdIcon>{fieldIcons.custom}</TdIcon>
                  <TdKey>{key}</TdKey>
                  <TdValue>{value}</TdValue>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </>
    </Box>
  );
}

function TdKey({ children }: { children: string }) {
  return (
    <Td pl={2} width="25%">
      <Text noOfLines={1} title={children}>
        {children}
      </Text>
    </Td>
  );
}

function TdIcon({ children }: { children: React.ReactNode }) {
  return (
    <Td color="dimmed" width={3}>
      {children}
    </Td>
  );
}

function TdValue({ children }: { children: React.ReactNode }) {
  return (
    <Td>
      <Text
        noOfLines={3}
        title={typeof children === "string" ? children : undefined}
      >
        {children}
      </Text>
    </Td>
  );
}
